import { useState, useEffect, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import constants from '@playcastdotio/GlobalConfig';
import { send } from '../../../../api/modules/apiV2';
import { gamingScoreCoordinator } from '../../../../coordinators/init';
import { GamingScoreTimeoutError, shouldThrottleGameScoreError } from '../../../../coordinators/gamingScore/errors';
import {
  LOCAL_SHELL_TIER_OVERRIDE,
  LOCAL_TEST_AS_MAX_TIER,
  buildSimulatedTierAssignment,
} from '../tier0/tierShellDevOverrides';
import { shouldUseLocalShellTierOverride } from '../tier0/rewardsShellDevMode';
import { hasUnknownEligibility, isOpenReady } from './tierDetailSession';

interface TierInfo {
  tierId: string;
  name: string;
  canEarn: boolean;
  priority?: number;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
  detailSummary?: string;
  componentChecks?: ComponentCheck[];
  /**
   * Capability from the stored tier (redacted host read). Absent means allowed.
   * The shell uses this for the entry OFFER; the service gate is the authority.
   */
  hostModeAllowed?: boolean;
  /** Disclosed eligibility rows from the host read. Hidden rows are already omitted. */
  conditions?: Array<{
    type: 'supportedOs' | 'allowedCountry' | 'machineType';
    status: 'pass' | 'fail' | 'unknown' | 'skipped';
    observedValue?: string;
    requirementText?: string;
  }>;
}

export interface ComponentCheck {
  component: string;
  label: string;
  currentValue: string;
  score: number;
  requiredScore: number;
  passes: boolean;
  /** English sentence under a failed eligibility row. */
  failHint?: string;
}

interface NextTierInfo {
  tierId: string;
  name: string;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
  componentChecks: ComponentCheck[];
}

export interface PreviousTierInfo {
  tierId: string;
  name: string;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
}

interface UseTierEvaluationResult {
  tier: TierInfo | null;
  nextTier: NextTierInfo | null;
  previousTier: PreviousTierInfo | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
  initialFetchDone: boolean;
  initialFetchFailed: boolean;
}

interface TierDefinition {
  tierId: string;
  name: string;
  priority: number;
  canEarn: boolean;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
  detailSummary?: string;
  nextTierEarnRateDescription?: string;
  nextTierMaxEarningsDescription?: string;
  requirements?: Record<string, { min?: number }>;
}

const COMPONENT_LABELS: Record<string, string> = {
  cpu: 'CPU',
  gpu: 'GPU',
  memory: 'Memory',
  storage: 'Storage',
  network: 'Network',
  videoEncoding: 'Video Encoding',
  environment: 'Environment',
  trust: 'Trust',
  osVersion: 'OS Version',
};

/**
 * On local dev, PlayJector never sends metrics so machineTierDetail is never
 * populated. This fetches tier definitions directly and builds synthetic
 * display data so the Welcome / About Tiers pages are testable.
 *
 * For UI shell work, prefer `LOCAL_SHELL_TIER_OVERRIDE` in tierShellDevOverrides.ts.
 */
// TESTING: Set to true to skip the local fallback and see the full loading screen
const LOCAL_SKIP_FALLBACK = true;

function parseTierDefinitions(tiers: TierDefinition[]): {
  tier: TierInfo;
  nextTier: NextTierInfo | null;
  previousTier: PreviousTierInfo | null;
} {
  const sorted = [...tiers].sort((a, b) => a.priority - b.priority);

  if (LOCAL_TEST_AS_MAX_TIER && sorted.length > 1) {
    const last = sorted[sorted.length - 1];
    const prev = sorted[sorted.length - 2];

    const checks: ComponentCheck[] = [];
    if (last.requirements) {
      for (const [key, req] of Object.entries(last.requirements)) {
        if (req?.min && req.min > 0) {
          checks.push({
            component: key,
            label: COMPONENT_LABELS[key] ?? key,
            currentValue: 'N/A (local)',
            score: req.min,
            requiredScore: req.min,
            passes: true,
          });
        }
      }
    }

    const tier: TierInfo = {
      tierId: last.tierId,
      name: last.name,
      canEarn: last.canEarn,
      priority: last.priority,
      earnRateDescription: last.earnRateDescription,
      maxEarningsDescription: last.maxEarningsDescription,
      detailSummary: last.detailSummary,
      componentChecks: checks,
    };

    const previousTier: PreviousTierInfo = {
      tierId: prev.tierId,
      name: prev.name,
      earnRateDescription: prev.earnRateDescription,
      maxEarningsDescription: prev.maxEarningsDescription,
    };

    return { tier, nextTier: null, previousTier };
  }

  const first = sorted[0];
  const second = sorted.length > 1 ? sorted[1] : null;

  const tier: TierInfo = {
    tierId: first?.tierId ?? 'tier-1',
    name: first?.name ?? 'Tier 1',
    canEarn: first?.canEarn ?? true,
    priority: first?.priority ?? 0,
    earnRateDescription: first?.earnRateDescription,
    maxEarningsDescription: first?.maxEarningsDescription,
    detailSummary: first?.detailSummary,
  };

  let nextTier: NextTierInfo | null = null;
  if (second) {
    const checks: ComponentCheck[] = [];
    if (second.requirements) {
      for (const [key, req] of Object.entries(second.requirements)) {
        if (req?.min && req.min > 0) {
          checks.push({
            component: key,
            label: COMPONENT_LABELS[key] ?? key,
            currentValue: 'N/A (local)',
            score: 0,
            requiredScore: req.min,
            passes: false,
          });
        }
      }
    }
    nextTier = {
      tierId: second.tierId,
      name: second.name,
      earnRateDescription: second.nextTierEarnRateDescription ?? second.earnRateDescription,
      maxEarningsDescription: second.nextTierMaxEarningsDescription ?? second.maxEarningsDescription,
      componentChecks: checks,
    };
  }

  return { tier, nextTier, previousTier: null };
}

const TIER_TIMEOUT_MS = 150_000;

const LOCAL_RETRY_DELAYS_MS = [2000, 4000, 8000] as const;

/**
 * Hardcoded dummy tiers for local dev. Used as the final fallback when the
 * realtime-api tier-definition endpoint is unavailable/empty (it is behind
 * authMiddleware and needs tiers seeded), so the local Welcome page always
 * renders a Tier 1 card instead of hanging or erroring.
 */
const LOCAL_DUMMY_TIER_DEFINITIONS: TierDefinition[] = [
  {
    tierId: 'tier-1',
    name: 'Tier 1',
    priority: 1,
    canEarn: true,
    earnRateDescription: 'Earn 1 point per hour',
    maxEarningsDescription: 'Earn up to $40/month',
    detailSummary: 'Basic tier for all machines',
    requirements: {},
  },
  {
    tierId: 'tier-2',
    name: 'Tier 2',
    priority: 2,
    canEarn: true,
    nextTierEarnRateDescription: 'Earn 2 points per hour',
    nextTierMaxEarningsDescription: 'Earn up to $80/month',
    requirements: { cpu: { min: 1 }, gpu: { min: 1 }, memory: { min: 1 }, storage: { min: 1 } },
  },
];

async function fetchLocalFallbackTiersWithRetry(): Promise<{ tier: TierInfo; nextTier: NextTierInfo | null; previousTier: PreviousTierInfo | null }> {
  for (let attempt = 0; attempt <= LOCAL_RETRY_DELAYS_MS.length; attempt++) {
    try {
      const tiers: TierDefinition[] = await send('get', 'realtime/admin/rewards/tiers');
      if (Array.isArray(tiers) && tiers.length > 0) {
        return parseTierDefinitions(tiers);
      }
    } catch {
      if (attempt < LOCAL_RETRY_DELAYS_MS.length) {
        const delay = LOCAL_RETRY_DELAYS_MS[attempt];
        console.debug(`[useTierEvaluation] Local tier fetch attempt ${attempt + 1} failed, retrying in ${delay}ms…`);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
  }
  console.debug('[useTierEvaluation] Local tier definitions unavailable — using hardcoded dummy tiers');
  return parseTierDefinitions(LOCAL_DUMMY_TIER_DEFINITIONS);
}

function applyTierDetail(
  detail: any,
  setTier: (t: TierInfo) => void,
  setNextTier: (t: NextTierInfo | null) => void,
  setPreviousTier: (t: PreviousTierInfo | null) => void,
): void {
  setTier({
    tierId: detail.assignedTierId,
    name: detail.assignedTierName,
    canEarn: detail.canEarn,
    priority: detail.priority,
    earnRateDescription: detail.earnRateDescription,
    maxEarningsDescription: detail.maxEarningsDescription,
    detailSummary: detail.detailSummary,
    componentChecks: Array.isArray(detail.componentChecks) ? detail.componentChecks : [],
    // Absent means allowed; the shell only needs to know about an explicit false.
    hostModeAllowed: detail.hostModeAllowed !== false,
    conditions: Array.isArray(detail.conditions) ? detail.conditions : [],
  });
  setNextTier(detail.nextTier ? {
    tierId: detail.nextTier.tierId,
    name: detail.nextTier.name,
    earnRateDescription: detail.nextTier.earnRateDescription,
    maxEarningsDescription: detail.nextTier.maxEarningsDescription,
    componentChecks: Array.isArray(detail.nextTier.componentChecks)
      ? detail.nextTier.componentChecks
      : [],
  } : null);
  setPreviousTier(detail.previousTier ? {
    tierId: detail.previousTier.tierId,
    name: detail.previousTier.name,
    earnRateDescription: detail.previousTier.earnRateDescription,
    maxEarningsDescription: detail.previousTier.maxEarningsDescription,
  } : null);
}

/**
 * Fetches tier data from the Machine record, mirroring how the admin-dashboard
 * fetches machine details via fetchMachineDetail(machineId).
 *
 * The tier detail is pre-computed by TierEvaluationService.evaluate() whenever
 * the gaming score is computed, and persisted as `machineTierDetail` on the
 * Machine document. This hook simply reads that field.
 *
 * If the field is not yet populated (fresh install — score hasn't been computed
 * yet), the hook subscribes to GamingScoreCoordinator's `authoritativeScoreReceived`
 * event and re-fetches once the score arrives, keeping `loading = true` until
 * real data is available.
 *
 * On local dev (no PlayJector), falls back to fetching tier definitions
 * directly so the Welcome / About Tiers pages are testable.
 */
export function useTierEvaluation(machineId: string | null): UseTierEvaluationResult {
  const [tier, setTier] = useState<TierInfo | null>(null);
  const [nextTier, setNextTier] = useState<NextTierInfo | null>(null);
  const [previousTier, setPreviousTier] = useState<PreviousTierInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [initialFetchFailed, setInitialFetchFailed] = useState(false);
  const unsubRef = useRef<(() => void) | null>(null);

  const retry = useCallback(() => {
    console.debug('[useTierEvaluation] Manual retry triggered');
    setLoading(false);
    setRetryCount((c) => c + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let resolved = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let pollId: ReturnType<typeof setInterval> | null = null;
    let announcedOpen = false;

    const cleanupListener = () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };

    const cleanupTimeout = () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const fetchTierDetail = async (): Promise<'found' | 'found-unknown' | 'pending' | 'not-found' | 'error'> => {
      try {
        const announce = !announcedOpen;
        announcedOpen = true;
        const tierUrl = `realtime/rewards/machine-tier/${encodeURIComponent(machineId!)}${announce ? '?resetOpen=1' : ''}`;
        const data = await send('get', tierUrl);
        if (cancelled) return 'error';

        const detail = data?.tierDetail;
        if (isOpenReady(data) && detail) {
          applyTierDetail(detail, setTier, setNextTier, setPreviousTier);
          setError(null);
          return hasUnknownEligibility(detail) ? 'found-unknown' : 'found';
        }
        return detail ? 'pending' : 'not-found';
      } catch {
        return 'error';
      }
    };

    const showDetail = (kind: 'found' | 'found-unknown') => {
      if (kind === 'found') {
        resolved = true;
        cleanupListener();
        cleanupTimeout();
        if (pollId !== null) clearInterval(pollId);
      }
      setLoading(false);
    };

    const handleTimeout = async () => {
      timeoutId = null;
      if (cancelled || resolved) return;

      const timeoutErr = new GamingScoreTimeoutError(machineId!, {
        hasAuthoritative: gamingScoreCoordinator.hasAuthoritative(),
        rawMetricsReceived: gamingScoreCoordinator.getRawMetricsTimestamp() !== null,
        elapsedMs: TIER_TIMEOUT_MS,
      });
      if (!shouldThrottleGameScoreError(timeoutErr.name, machineId!)) {
        Sentry.captureException(timeoutErr, { extra: timeoutErr.context });
      }

      try {
        const data = await send('post', `realtime/rewards/machine-tier/${encodeURIComponent(machineId!)}/fallback`);
        if (cancelled || resolved) return;
        const detail = data?.tierDetail;
        if (data?.pending && !isOpenReady(data)) return;
        if (detail) {
          applyTierDetail(detail, setTier, setNextTier, setPreviousTier);
          setError(null);
          showDetail(hasUnknownEligibility(detail) ? 'found-unknown' : 'found');
          return;
        }
        setError('Fallback did not return tier data');
      } catch {
        if (cancelled) return;
        setError('Failed to load tier data');
      }
      showDetail('found');
    };

    const run = async () => {
      if (!machineId) return;

      if (!cancelled) {
        setLoading(true);
        setInitialFetchDone(false);
        setInitialFetchFailed(false);
      }

      if (shouldUseLocalShellTierOverride()) {
        const assignedId = LOCAL_SHELL_TIER_OVERRIDE ?? 'tier-0';
        const simulated = buildSimulatedTierAssignment(assignedId);
        if (!cancelled) {
          setTier(simulated.tier);
          setNextTier(simulated.nextTier);
          setPreviousTier(simulated.previousTier);
          setError(null);
          setInitialFetchDone(true);
          setInitialFetchFailed(false);
          setLoading(false);
        }
        console.debug('[useTierEvaluation] Shell tier override — assigned tier:', assignedId);
        return;
      }

      const fetchResult = await fetchTierDetail();
      if (cancelled) return;
      setInitialFetchDone(true);
      if (fetchResult === 'error') setInitialFetchFailed(true);

      if (fetchResult === 'found' || fetchResult === 'found-unknown') {
        showDetail(fetchResult);
        if (fetchResult === 'found') return;
      }

      if (fetchResult === 'not-found' && constants.BRANCH === 'local' && !LOCAL_SKIP_FALLBACK) {
        console.debug('[useTierEvaluation] No machineTierDetail — using local tier definition fallback');
        try {
          const fallback = await fetchLocalFallbackTiersWithRetry();
          if (cancelled) return;
          resolved = true;
          setTier(fallback.tier);
          setNextTier(fallback.nextTier);
          setPreviousTier(fallback.previousTier);
          setError(null);
          setLoading(false);
          return;
        } catch {
          if (cancelled) return;
          resolved = true;
          console.debug('[useTierEvaluation] Local fallback failed after retries');
          setError('Failed to load tier data');
          setLoading(false);
          return;
        }
      }

      cleanupListener();
      unsubRef.current = gamingScoreCoordinator.on('authoritativeScoreReceived', async () => {
        if (cancelled || resolved) return;
        const result = await fetchTierDetail();
        if (cancelled || resolved) return;
        if (result === 'found' || result === 'found-unknown') showDetail(result);
      });

      pollId = setInterval(() => {
        void (async () => {
          if (cancelled || resolved) return;
          const result = await fetchTierDetail();
          if (cancelled || resolved) return;
          if (result === 'found' || result === 'found-unknown') showDetail(result);
        })();
      }, 2000);

      timeoutId = setTimeout(() => {
        void handleTimeout();
      }, TIER_TIMEOUT_MS);
    };

    run();
    return () => {
      cancelled = true;
      cleanupListener();
      cleanupTimeout();
      if (pollId !== null) clearInterval(pollId);
    };
  }, [machineId, retryCount]);

  return { tier, nextTier, previousTier, loading, error, retry, initialFetchDone, initialFetchFailed };
}
